// src/components/ICT/Header.js

import React, { useState, useEffect, useRef } from 'react';
import { 
  FiSearch, 
  FiUser, 
  FiLogOut, 
  FiSettings, 
  FiLock,
  FiChevronDown,
  FiMoon,
  FiSun,
  FiUserCheck,
  FiHelpCircle,
  FiMail,
  FiCalendar,
  FiAlertCircle,
  FiGrid,
  FiBarChart2,
  FiUsers,
  FiClock,
  FiAward,
  FiX,
  FiMapPin,
  FiServer,
  FiShield,
  FiCpu,
  FiWifi,
  FiHardDrive,
  FiCloud,
  FiCode,
  FiDatabase,
  FiEye,
  FiEyeOff,
  FiCheckCircle,
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';

const Header = () => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [showChangePasswordDialog, setShowChangePasswordDialog] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [systemStatus, setSystemStatus] = useState({
    servers: 'online',
    network: 'online',
    database: 'online',
    security: 'online',
  });
  const [userData, setUserData] = useState({
    name: '',
    email: '',
    role: '',
    department: '',
    staffId: '',
    phone: '',
    location: '',
    itRole: '',
  });

  // Change Password state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordErrors, setPasswordErrors] = useState({});
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    label: '',
    color: '',
  });
  
  const navigate = useNavigate();
  const menuRef = useRef(null);
  const dialogRef = useRef(null);
  const passwordDialogRef = useRef(null);

  // Load user data from localStorage
  useEffect(() => {
    // Get user data with proper fallbacks
    const firstName = localStorage.getItem('first_name') || '';
    const lastName = localStorage.getItem('last_name') || '';
    const fullName = localStorage.getItem('user_name') || 
                     localStorage.getItem('userName') || 
                     (firstName && lastName ? `${firstName} ${lastName}`.trim() : firstName) ||
                     'IT Staff';
    
    const email = localStorage.getItem('user_email') || 
                  localStorage.getItem('email') || 
                  '';
    
    // Get role with proper case handling
    const rawRole = localStorage.getItem('user_role') || 
                    localStorage.getItem('role') || 
                    'IT Staff';
    const roleLower = rawRole.toLowerCase();
    const roleDisplay = rawRole.charAt(0).toUpperCase() + rawRole.slice(1);
    
    const department = localStorage.getItem('department') || 
                       localStorage.getItem('userDepartment') || 
                       'ICT';
    
    const staffId = localStorage.getItem('staff_id') || 
                    localStorage.getItem('staffId') || 
                    '';
    
    const phone = localStorage.getItem('phone') || 
                  localStorage.getItem('phoneNumber') || 
                  '';
    
    // ICT specific role
    const itRole = localStorage.getItem('it_role') || 
                   localStorage.getItem('ict_role') || 
                   'IT Staff';

    setUserData({
      name: fullName || 'IT Staff',
      email,
      role: roleDisplay,
      roleLower: roleLower,
      department,
      staffId,
      phone,
      location: 'ICT Department',
      itRole: itRole,
    });
  }, []);

  // Digital clock update
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Simulate system status monitoring
  useEffect(() => {
    const statusTimer = setInterval(() => {
      // Randomly update system status for demo (simulate real monitoring)
      const statuses = ['online', 'online', 'online', 'degraded', 'online'];
      setSystemStatus({
        servers: statuses[Math.floor(Math.random() * 5)],
        network: statuses[Math.floor(Math.random() * 4)],
        database: statuses[Math.floor(Math.random() * 5)],
        security: statuses[Math.floor(Math.random() * 4)],
      });
    }, 30000); // Update every 30 seconds

    return () => clearInterval(statusTimer);
  }, []);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowProfileMenu(false);
        setShowSettings(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Prevent body scroll when dialogs are open
  useEffect(() => {
    if (showLogoutDialog || showChangePasswordDialog) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showLogoutDialog, showChangePasswordDialog]);

  // Format time
  const formatTime = (date) => {
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${hours}:${minutes}:${seconds}`;
  };

  const formatDate = (date) => {
    const options = {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    };
    return date.toLocaleDateString('en-US', options);
  };

  // Navigation handlers
  const handleLogout = () => {
    setShowLogoutDialog(true);
    setShowProfileMenu(false);
  };

  const confirmLogout = () => {
    // Clear all auth data
    const keys = [
      'access_token', 'refresh_token', 'user_data', 'user_email', 'user_role',
      'user_id', 'user_name', 'first_name', 'staff_id', 'department', 'phone',
      'gender', 'is_active', 'user_location', 'location', 'email', 'role',
      'userId', 'staffId', 'userDepartment', 'phoneNumber', 'it_role', 'ict_role'
    ];
    keys.forEach(key => localStorage.removeItem(key));
    
    navigate('/ict_LoginPage');
    setShowLogoutDialog(false);
  };

  const cancelLogout = () => {
    setShowLogoutDialog(false);
  };

  // Change Password handlers
  const handleChangePasswordClick = () => {
    setShowChangePasswordDialog(true);
    setShowProfileMenu(false);
    // Reset form
    setPasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    });
    setPasswordErrors({});
    setPasswordSuccess(false);
    setPasswordStrength({ score: 0, label: '', color: '' });
  };

  const handlePasswordChange = (field, value) => {
    setPasswordData(prev => ({ ...prev, [field]: value }));
    if (passwordErrors[field]) {
      setPasswordErrors(prev => ({ ...prev, [field]: '' }));
    }

    // Check password strength for new password
    if (field === 'newPassword' && value) {
      setPasswordStrength(checkPasswordStrength(value));
    }
  };

  const checkPasswordStrength = (password) => {
    let score = 0;
    if (password.length >= 8) score++;
    if (password.length >= 12) score++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
    if (/\d/.test(password)) score++;
    if (/[^a-zA-Z0-9]/.test(password)) score++;

    const strengths = [
      { score: 0, label: 'Very Weak', color: '#F44336' },
      { score: 1, label: 'Weak', color: '#FF9800' },
      { score: 2, label: 'Fair', color: '#FFC107' },
      { score: 3, label: 'Good', color: '#8BC34A' },
      { score: 4, label: 'Strong', color: '#4CAF50' },
      { score: 5, label: 'Very Strong', color: '#2E7D32' },
    ];

    const result = strengths.find(s => s.score === score) || strengths[0];
    return result;
  };

  const validatePasswordForm = () => {
    const errors = {};

    if (!passwordData.currentPassword) {
      errors.currentPassword = 'Current password is required';
    }

    if (!passwordData.newPassword) {
      errors.newPassword = 'New password is required';
    } else if (passwordData.newPassword.length < 8) {
      errors.newPassword = 'Password must be at least 8 characters';
    } else if (!/[a-z]/.test(passwordData.newPassword) || !/[A-Z]/.test(passwordData.newPassword)) {
      errors.newPassword = 'Password must contain both uppercase and lowercase letters';
    } else if (!/\d/.test(passwordData.newPassword)) {
      errors.newPassword = 'Password must contain at least one number';
    }

    if (!passwordData.confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (passwordData.newPassword !== passwordData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    // Check if new password is same as current (client-side hint)
    if (passwordData.currentPassword && passwordData.newPassword && 
        passwordData.currentPassword === passwordData.newPassword) {
      errors.newPassword = 'New password must be different from current password';
    }

    setPasswordErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChangePassword = async () => {
    if (!validatePasswordForm()) {
      return;
    }

    setIsChangingPassword(true);

    try {
      // Get the auth token
      const token = localStorage.getItem('access_token');
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
      const API_PREFIX = process.env.REACT_APP_API_PREFIX || '/pos_v1';
      const FULL_API_URL = `${API_BASE_URL}${API_PREFIX}`;

      const response = await fetch(`${FULL_API_URL}/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          current_password: passwordData.currentPassword,
          new_password: passwordData.newPassword,
          confirm_new_password: passwordData.confirmPassword,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to change password');
      }

      // Success
      setPasswordSuccess(true);
      setTimeout(() => {
        setShowChangePasswordDialog(false);
        setPasswordSuccess(false);
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        });
      }, 2000);
    } catch (err) {
      setPasswordErrors({
        general: err.message || 'Failed to change password. Please try again.',
      });
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/ict/search?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  // Helper functions for role detection
  const isSysAdmin = () => {
    const role = userData.itRole?.toLowerCase() || '';
    return role === 'sysadmin' || role === 'system administrator' || 
           role === 'systems administrator';
  };

  const isNetworkEngineer = () => {
    const role = userData.itRole?.toLowerCase() || '';
    return role === 'network engineer' || role === 'network administrator';
  };

  const isSecurityEngineer = () => {
    const role = userData.itRole?.toLowerCase() || '';
    return role === 'security engineer' || role === 'cybersecurity analyst';
  };

  const isITSupport = () => {
    const role = userData.itRole?.toLowerCase() || '';
    return role === 'it support' || role === 'helpdesk' || role === 'support engineer';
  };

  // Get role badge color based on ICT role
  const getRoleColor = (role) => {
    const roleColors = {
      'SysAdmin': '#0A2463',
      'System Administrator': '#0A2463',
      'Network Engineer': '#00695C',
      'Network Administrator': '#00695C',
      'Security Engineer': '#C62828',
      'Cybersecurity Analyst': '#C62828',
      'IT Support': '#4A148C',
      'Helpdesk': '#4A148C',
      'Support Engineer': '#4A148C',
      'DevOps': '#E65100',
      'Developer': '#1565C0',
      'Database Admin': '#00838F',
      'IT Staff': '#1E88E5',
    };
    return roleColors[role] || '#1E88E5';
  };

  // Get status indicator color
  const getStatusColor = (status) => {
    switch(status) {
      case 'online': return '#22c55e';
      case 'degraded': return '#eab308';
      case 'offline': return '#ef4444';
      default: return '#94a3b8';
    }
  };

  const userInitial = userData.name ? userData.name.charAt(0).toUpperCase() : 'I';

  // Get system status summary
  const getSystemStatusSummary = () => {
    const statuses = Object.values(systemStatus);
    const onlineCount = statuses.filter(s => s === 'online').length;
    const totalCount = statuses.length;
    
    if (onlineCount === totalCount) {
      return { text: 'All Systems Operational', color: '#22c55e', icon: '✅' };
    } else if (onlineCount >= totalCount / 2) {
      return { text: 'Some Systems Degraded', color: '#eab308', icon: '⚠️' };
    } else {
      return { text: 'Critical System Issues', color: '#ef4444', icon: '🚨' };
    }
  };

  const systemStatusSummary = getSystemStatusSummary();

  return (
    <>
      <header className="ict-header">
        {/* Search */}
        <div className="search-box">
          <FiSearch size={18} className="search-icon" />
          <form onSubmit={handleSearch} className="search-form">
            <input 
              placeholder="Search logs, systems, devices..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button 
                type="button" 
                onClick={() => setSearchQuery('')}
                className="search-clear"
              >
                ✕
              </button>
            )}
          </form>
        </div>

        {/* Center - System Status & Digital Clock */}
        <div className="header-center">
          <div className="system-status-badge">
            <span className="status-indicator" style={{ 
              backgroundColor: systemStatusSummary.color 
            }}></span>
            <span className="status-text">{systemStatusSummary.icon} {systemStatusSummary.text}</span>
          </div>
          
          <div className="digital-clock">
            <span className="clock-icon">🖥️</span>
            <span className="clock-time">{formatTime(currentTime)}</span>
            <span className="clock-divider">|</span>
            <span className="clock-date">{formatDate(currentTime)}</span>
          </div>
        </div>

        {/* Right Section */}
        <div className="header-right">
          {/* Quick Status Icons */}
          <div className="quick-status-icons">
            <div className="status-icon" title="Servers">
              <FiServer size={16} />
              <span className="status-dot" style={{ backgroundColor: getStatusColor(systemStatus.servers) }}></span>
            </div>
            <div className="status-icon" title="Network">
              <FiWifi size={16} />
              <span className="status-dot" style={{ backgroundColor: getStatusColor(systemStatus.network) }}></span>
            </div>
            <div className="status-icon" title="Database">
              <FiDatabase size={16} />
              <span className="status-dot" style={{ backgroundColor: getStatusColor(systemStatus.database) }}></span>
            </div>
            <div className="status-icon" title="Security">
              <FiShield size={16} />
              <span className="status-dot" style={{ backgroundColor: getStatusColor(systemStatus.security) }}></span>
            </div>
          </div>

          {/* Profile */}
          <div 
            className="profile-wrapper"
            ref={menuRef}
          >
            <div 
              className="profile"
              onClick={() => setShowProfileMenu(!showProfileMenu)}
            >
              <div className="avatar-wrapper">
                <div className="avatar">{userInitial}</div>
                <div className="avatar-status online"></div>
              </div>
              <div className="profile-info">
                <div className="profile-name">{userData.name}</div>
                <div className="profile-role">
                  <span 
                    className="role-badge"
                    style={{ backgroundColor: getRoleColor(userData.itRole) }}
                  >
                    {userData.itRole}
                  </span>
                </div>
              </div>
              <FiChevronDown size={16} className="profile-arrow" />
            </div>

            {/* Profile Dropdown */}
            {showProfileMenu && (
              <div className="profile-dropdown">
                <div className="dropdown-user-info">
                  <div className="dropdown-avatar">{userInitial}</div>
                  <div>
                    <div className="dropdown-name">{userData.name}</div>
                    <div className="dropdown-email">{userData.email}</div>
                  </div>
                </div>

                <div className="dropdown-badge">
                  <FiGrid size={14} />
                  <span>{userData.department} Department</span>
                </div>

                <div className="dropdown-badge" style={{ 
                  background: '#e3f2fd',
                  color: '#0A2463',
                }}>
                  <FiUserCheck size={14} />
                  <span>{userData.itRole}</span>
                </div>

                {userData.staffId && (
                  <div className="dropdown-badge">
                    <FiAward size={14} />
                    <span>Staff ID: {userData.staffId}</span>
                  </div>
                )}

                <div className="dropdown-divider"></div>

                {/* System Status in Dropdown */}
                <div className="dropdown-status">
                  <div className="status-header">System Status</div>
                  <div className="status-grid">
                    <div className="status-item">
                      <FiServer size={14} />
                      <span>Servers</span>
                      <span className="status-label" style={{ color: getStatusColor(systemStatus.servers) }}>
                        {systemStatus.servers}
                      </span>
                    </div>
                    <div className="status-item">
                      <FiWifi size={14} />
                      <span>Network</span>
                      <span className="status-label" style={{ color: getStatusColor(systemStatus.network) }}>
                        {systemStatus.network}
                      </span>
                    </div>
                    <div className="status-item">
                      <FiDatabase size={14} />
                      <span>Database</span>
                      <span className="status-label" style={{ color: getStatusColor(systemStatus.database) }}>
                        {systemStatus.database}
                      </span>
                    </div>
                    <div className="status-item">
                      <FiShield size={14} />
                      <span>Security</span>
                      <span className="status-label" style={{ color: getStatusColor(systemStatus.security) }}>
                        {systemStatus.security}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="dropdown-divider"></div>

                <div 
                  className="dropdown-item"
                  onClick={handleChangePasswordClick}
                >
                  <FiLock size={16} />
                  <span>Change Password</span>
                </div>

                <div className="dropdown-divider"></div>

                <div 
                  className="dropdown-item logout"
                  onClick={handleLogout}
                >
                  <FiLogOut size={16} />
                  <span>Logout</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Custom Logout Confirmation Dialog */}
      {showLogoutDialog && (
        <div className="dialog-overlay" onClick={cancelLogout}>
          <div className="dialog-container" ref={dialogRef} onClick={(e) => e.stopPropagation()}>
            <div className="dialog-content">
              <div className="dialog-icon-wrapper">
                <div className="dialog-icon">
                  <FiLogOut size={32} />
                </div>
              </div>
              
              <h3 className="dialog-title">Confirm Logout</h3>
              
              <p className="dialog-message">
                Are you sure you want to logout? You will need to login again to access your ICT dashboard.
              </p>

              <div className="dialog-user-info">
                <div className="dialog-user-avatar">{userInitial}</div>
                <div className="dialog-user-details">
                  <div className="dialog-user-name">{userData.name}</div>
                  <div className="dialog-user-email">{userData.email}</div>
                  <div className="dialog-user-role">
                    <FiCpu size={12} />
                    <span>{userData.itRole}</span>
                  </div>
                </div>
              </div>

              <div className="dialog-actions">
                <button 
                  className="dialog-btn dialog-btn-cancel"
                  onClick={cancelLogout}
                >
                  Cancel
                </button>
                <button 
                  className="dialog-btn dialog-btn-confirm"
                  onClick={confirmLogout}
                >
                  <FiLogOut size={16} />
                  Logout
                </button>
              </div>
            </div>

            <button className="dialog-close" onClick={cancelLogout}>
              <FiX size={20} />
            </button>
          </div>
        </div>
      )}

      {/* Change Password Dialog */}
      {showChangePasswordDialog && (
        <div className="dialog-overlay" onClick={() => setShowChangePasswordDialog(false)}>
          <div className="dialog-container password-dialog" ref={passwordDialogRef} onClick={(e) => e.stopPropagation()}>
            <div className="dialog-content">
              <div className="dialog-icon-wrapper password-icon-wrapper">
                <div className="dialog-icon password-icon">
                  <FiLock size={32} />
                </div>
              </div>
              
              <h3 className="dialog-title">Change Password</h3>
              
              <p className="dialog-message">
                Enter your current password and choose a new password to update your account security.
              </p>

              {passwordErrors.general && (
                <div className="password-error-general">
                  <FiAlertCircle size={16} />
                  <span>{passwordErrors.general}</span>
                </div>
              )}

              {passwordSuccess && (
                <div className="password-success">
                  <FiCheckCircle size={18} />
                  <span>Password changed successfully! Redirecting...</span>
                </div>
              )}

              <div className="password-form">
                <div className="password-field">
                  <label className="password-label">Current Password</label>
                  <div className="password-input-wrapper">
                    <input
                      type={showCurrentPassword ? 'text' : 'password'}
                      className={`password-input ${passwordErrors.currentPassword ? 'error' : ''}`}
                      placeholder="Enter current password"
                      value={passwordData.currentPassword}
                      onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
                      disabled={passwordSuccess}
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    >
                      {showCurrentPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                    </button>
                  </div>
                  {passwordErrors.currentPassword && (
                    <span className="password-error">{passwordErrors.currentPassword}</span>
                  )}
                </div>

                <div className="password-field">
                  <label className="password-label">New Password</label>
                  <div className="password-input-wrapper">
                    <input
                      type={showNewPassword ? 'text' : 'password'}
                      className={`password-input ${passwordErrors.newPassword ? 'error' : ''}`}
                      placeholder="Enter new password (min. 8 characters)"
                      value={passwordData.newPassword}
                      onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                      disabled={passwordSuccess}
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                    >
                      {showNewPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                    </button>
                  </div>
                  {passwordErrors.newPassword && (
                    <span className="password-error">{passwordErrors.newPassword}</span>
                  )}
                  
                  {/* Password Strength Indicator */}
                  {passwordData.newPassword && !passwordErrors.newPassword && (
                    <div className="password-strength">
                      <div className="strength-bar-container">
                        <div 
                          className="strength-bar"
                          style={{
                            width: `${(passwordStrength.score / 5) * 100}%`,
                            backgroundColor: passwordStrength.color,
                          }}
                        />
                      </div>
                      <span className="strength-label" style={{ color: passwordStrength.color }}>
                        {passwordStrength.label}
                      </span>
                    </div>
                  )}
                </div>

                <div className="password-field">
                  <label className="password-label">Confirm New Password</label>
                  <div className="password-input-wrapper">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      className={`password-input ${passwordErrors.confirmPassword ? 'error' : ''}`}
                      placeholder="Confirm new password"
                      value={passwordData.confirmPassword}
                      onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                      disabled={passwordSuccess}
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                    </button>
                  </div>
                  {passwordErrors.confirmPassword && (
                    <span className="password-error">{passwordErrors.confirmPassword}</span>
                  )}
                </div>

                <div className="password-requirements">
                  <div className={`requirement ${passwordData.newPassword.length >= 8 ? 'met' : ''}`}>
                    <span className="requirement-icon">
                      {passwordData.newPassword.length >= 8 ? '✓' : '○'}
                    </span>
                    <span>At least 8 characters</span>
                  </div>
                  <div className={`requirement ${/[a-z]/.test(passwordData.newPassword) && /[A-Z]/.test(passwordData.newPassword) ? 'met' : ''}`}>
                    <span className="requirement-icon">
                      {/[a-z]/.test(passwordData.newPassword) && /[A-Z]/.test(passwordData.newPassword) ? '✓' : '○'}
                    </span>
                    <span>Uppercase & lowercase letters</span>
                  </div>
                  <div className={`requirement ${/\d/.test(passwordData.newPassword) ? 'met' : ''}`}>
                    <span className="requirement-icon">
                      {/\d/.test(passwordData.newPassword) ? '✓' : '○'}
                    </span>
                    <span>At least one number</span>
                  </div>
                </div>
              </div>

              <div className="dialog-actions">
                <button 
                  className="dialog-btn dialog-btn-cancel"
                  onClick={() => {
                    setShowChangePasswordDialog(false);
                    setPasswordData({
                      currentPassword: '',
                      newPassword: '',
                      confirmPassword: '',
                    });
                    setPasswordErrors({});
                    setPasswordSuccess(false);
                  }}
                  disabled={isChangingPassword}
                >
                  Cancel
                </button>
                <button 
                  className="dialog-btn dialog-btn-confirm password-confirm-btn"
                  onClick={handleChangePassword}
                  disabled={isChangingPassword || passwordSuccess}
                >
                  {isChangingPassword ? (
                    <span className="loading-spinner" />
                  ) : (
                    <FiLock size={16} />
                  )}
                  {isChangingPassword ? 'Changing...' : 'Change Password'}
                </button>
              </div>
            </div>

            <button className="dialog-close" onClick={() => {
              setShowChangePasswordDialog(false);
              setPasswordData({
                currentPassword: '',
                newPassword: '',
                confirmPassword: '',
              });
              setPasswordErrors({});
              setPasswordSuccess(false);
            }}>
              <FiX size={20} />
            </button>
          </div>
        </div>
      )}

      {/* Styles */}
      <style>{`
        /* Header styles - ICT Theme */
        .ict-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 32px;
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(20px);
          border-bottom: 2px solid rgba(10, 36, 99, 0.08);
          box-shadow: 0 2px 20px rgba(0, 0, 0, 0.02);
          position: sticky;
          top: 0;
          z-index: 100;
          min-height: 72px;
          transition: all 0.3s ease;
        }

        .ict-header:hover {
          box-shadow: 0 4px 30px rgba(10, 36, 99, 0.06);
        }

        /* Search */
        .search-box {
          display: flex;
          align-items: center;
          background: #f1f3f5;
          border-radius: 12px;
          padding: 6px 16px;
          min-width: 280px;
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          border: 2px solid transparent;
        }

        .search-box:focus-within {
          background: #ffffff;
          border-color: #1E88E5;
          box-shadow: 0 0 0 4px rgba(30, 136, 229, 0.06);
          transform: scale(1.02);
        }

        .search-icon {
          color: #94a3b8;
          flex-shrink: 0;
          transition: color 0.3s ease;
        }

        .search-box:focus-within .search-icon {
          color: #1E88E5;
        }

        .search-form {
          flex: 1;
          display: flex;
          align-items: center;
        }

        .search-box input {
          border: none;
          outline: none;
          background: transparent;
          padding: 8px 12px;
          font-size: 13px;
          width: 100%;
          color: #1a1a1a;
          font-weight: 400;
          font-family: 'Courier New', monospace;
        }

        .search-box input::placeholder {
          color: #94a3b8;
          font-weight: 300;
        }

        .search-clear {
          background: none;
          border: none;
          color: #94a3b8;
          cursor: pointer;
          padding: 0 4px;
          font-size: 14px;
          transition: color 0.3s ease;
        }

        .search-clear:hover {
          color: #1a1a1a;
        }

        /* Center */
        .header-center {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .system-status-badge {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 6px 14px;
          border-radius: 20px;
          background: #f8f9fa;
          border: 1px solid rgba(0, 0, 0, 0.04);
          font-size: 12px;
          font-weight: 500;
        }

        .status-indicator {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          animation: pulse 2s infinite;
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }

        .status-text {
          color: #1a1a1a;
          font-size: 11px;
        }

        .digital-clock {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 6px 18px;
          border-radius: 10px;
          background: linear-gradient(135deg, rgba(10, 36, 99, 0.04), rgba(30, 136, 229, 0.02));
          border: 1px solid rgba(10, 36, 99, 0.06);
        }

        .clock-icon {
          font-size: 14px;
        }

        .clock-time {
          font-family: 'Courier New', monospace;
          font-size: 14px;
          font-weight: 600;
          color: #0A2463;
          letter-spacing: 1px;
        }

        .clock-divider {
          color: #94a3b8;
          font-size: 12px;
          opacity: 0.4;
        }

        .clock-date {
          font-size: 11px;
          color: #94a3b8;
          font-weight: 400;
        }

        /* Right Section */
        .header-right {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        /* Quick Status Icons */
        .quick-status-icons {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 4px 12px;
          border-radius: 12px;
          background: #f8f9fa;
          border: 1px solid rgba(0, 0, 0, 0.04);
        }

        .status-icon {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 28px;
          height: 28px;
          border-radius: 6px;
          background: transparent;
          color: #64748b;
          transition: all 0.3s ease;
          cursor: pointer;
        }

        .status-icon:hover {
          background: rgba(30, 136, 229, 0.08);
          color: #1E88E5;
          transform: scale(1.1);
        }

        .status-dot {
          position: absolute;
          bottom: 2px;
          right: 2px;
          width: 6px;
          height: 6px;
          border-radius: 50%;
          border: 1px solid white;
        }

        /* Profile */
        .profile-wrapper {
          position: relative;
        }

        .profile {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 4px 12px 4px 4px;
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.3s ease;
          background: transparent;
        }

        .profile:hover {
          background: #f1f3f5;
        }

        .avatar-wrapper {
          position: relative;
          flex-shrink: 0;
        }

        .avatar {
          width: 38px;
          height: 38px;
          border-radius: 50%;
          background: linear-gradient(135deg, #0A2463, #1E88E5);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          font-size: 14px;
          box-shadow: 0 2px 12px rgba(10, 36, 99, 0.2);
        }

        .avatar-status {
          position: absolute;
          bottom: 0;
          right: 0;
          width: 10px;
          height: 10px;
          border-radius: 50%;
          border: 2px solid white;
        }

        .avatar-status.online {
          background: #22c55e;
        }

        .profile-info {
          display: flex;
          flex-direction: column;
          line-height: 1.3;
        }

        .profile-name {
          font-size: 13px;
          font-weight: 600;
          color: #1a1a1a;
        }

        .profile-role {
          display: flex;
          align-items: center;
          gap: 4px;
          flex-wrap: wrap;
        }

        .role-badge {
          font-size: 10px;
          font-weight: 500;
          padding: 2px 8px;
          border-radius: 6px;
          color: white;
          letter-spacing: 0.3px;
        }

        .profile-arrow {
          color: #94a3b8;
          transition: transform 0.3s ease;
          flex-shrink: 0;
        }

        .profile:hover .profile-arrow {
          transform: rotate(180deg);
        }

        /* Profile Dropdown */
        .profile-dropdown {
          position: absolute;
          top: calc(100% + 12px);
          right: 0;
          background: rgba(255, 255, 255, 0.98);
          backdrop-filter: blur(20px);
          border-radius: 16px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.02);
          width: 300px;
          overflow: hidden;
          z-index: 1000;
          animation: slideDown 0.25s cubic-bezier(0.4, 0, 0.2, 1);
        }

        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-12px) scale(0.96);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        .dropdown-user-info {
          padding: 16px 20px;
          display: flex;
          align-items: center;
          gap: 14px;
          border-bottom: 1px solid rgba(0, 0, 0, 0.04);
        }

        .dropdown-avatar {
          width: 44px;
          height: 44px;
          border-radius: 50%;
          background: linear-gradient(135deg, #0A2463, #1E88E5);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          font-size: 16px;
          flex-shrink: 0;
        }

        .dropdown-name {
          font-size: 14px;
          font-weight: 600;
          color: #1a1a1a;
        }

        .dropdown-email {
          font-size: 12px;
          color: #94a3b8;
        }

        .dropdown-badge {
          padding: 8px 20px;
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 12px;
          color: #666;
          background: #f8f9fa;
          margin: 4px 12px;
          border-radius: 8px;
        }

        /* System Status in Dropdown */
        .dropdown-status {
          padding: 12px 20px;
        }

        .status-header {
          font-size: 11px;
          font-weight: 600;
          color: #94a3b8;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 8px;
        }

        .status-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 6px;
        }

        .status-item {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 4px 8px;
          background: #f8f9fa;
          border-radius: 6px;
          font-size: 11px;
          color: #1a1a1a;
        }

        .status-item .status-label {
          margin-left: auto;
          font-weight: 500;
          text-transform: capitalize;
          font-size: 10px;
        }

        .dropdown-divider {
          height: 1px;
          background: rgba(0, 0, 0, 0.04);
          margin: 4px 0;
        }

        .dropdown-item {
          padding: 10px 20px;
          display: flex;
          align-items: center;
          gap: 12px;
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 13px;
          color: #1a1a1a;
        }

        .dropdown-item:hover {
          background: #f8f9fa;
        }

        .dropdown-item.logout {
          color: #C62828;
        }

        .dropdown-item.logout:hover {
          background: #fef3f2;
        }

        /* Custom Logout Dialog */
        .dialog-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          backdrop-filter: blur(8px);
          z-index: 9999;
          display: flex;
          align-items: center;
          justify-content: center;
          animation: fadeIn 0.3s ease;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.9) translateY(20px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }

        .dialog-container {
          background: #ffffff;
          border-radius: 24px;
          padding: 40px 32px 32px;
          max-width: 440px;
          width: 90%;
          position: relative;
          box-shadow: 0 40px 80px rgba(0, 0, 0, 0.2);
          animation: scaleIn 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .password-dialog {
          max-width: 480px;
        }

        .dialog-close {
          position: absolute;
          top: 16px;
          right: 16px;
          background: none;
          border: none;
          color: #94a3b8;
          cursor: pointer;
          padding: 4px;
          border-radius: 8px;
          transition: all 0.3s ease;
        }

        .dialog-close:hover {
          background: #f1f3f5;
          color: #1a1a1a;
        }

        .dialog-content {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
        }

        .dialog-icon-wrapper {
          margin-bottom: 16px;
        }

        .password-icon-wrapper {
          margin-bottom: 12px;
        }

        .dialog-icon {
          width: 64px;
          height: 64px;
          border-radius: 50%;
          background: #fef3f2;
          color: #C62828;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .password-icon {
          background: #e3f2fd;
          color: #0A2463;
        }

        .dialog-title {
          font-size: 20px;
          font-weight: 700;
          color: #1a1a1a;
          margin: 0 0 8px 0;
        }

        .dialog-message {
          font-size: 14px;
          color: #666;
          line-height: 1.6;
          margin: 0 0 20px 0;
        }

        .dialog-user-info {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 16px;
          background: #f8f9fa;
          border-radius: 12px;
          width: 100%;
          margin-bottom: 24px;
        }

        .dialog-user-avatar {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: linear-gradient(135deg, #0A2463, #1E88E5);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          font-size: 14px;
          flex-shrink: 0;
        }

        .dialog-user-details {
          text-align: left;
          flex: 1;
        }

        .dialog-user-name {
          font-size: 14px;
          font-weight: 600;
          color: #1a1a1a;
        }

        .dialog-user-email {
          font-size: 12px;
          color: #94a3b8;
        }

        .dialog-user-role {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 11px;
          font-weight: 500;
          color: #1E88E5;
          margin-top: 2px;
        }

        .dialog-actions {
          display: flex;
          gap: 12px;
          width: 100%;
        }

        .dialog-btn {
          flex: 1;
          padding: 12px 20px;
          border: none;
          border-radius: 12px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }

        .dialog-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .dialog-btn-cancel {
          background: #f1f3f5;
          color: #666;
        }

        .dialog-btn-cancel:hover:not(:disabled) {
          background: #e5e7eb;
          transform: translateY(-2px);
        }

        .dialog-btn-confirm {
          background: linear-gradient(135deg, #0A2463, #1E88E5);
          color: white;
          box-shadow: 0 4px 16px rgba(10, 36, 99, 0.3);
        }

        .dialog-btn-confirm:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(10, 36, 99, 0.4);
        }

        .dialog-btn-confirm:active:not(:disabled) {
          transform: translateY(0);
        }

        .password-confirm-btn {
          background: linear-gradient(135deg, #0A2463, #1E88E5);
          box-shadow: 0 4px 16px rgba(10, 36, 99, 0.3);
        }

        .password-confirm-btn:hover:not(:disabled) {
          box-shadow: 0 8px 24px rgba(10, 36, 99, 0.4);
        }

        /* Password Form Styles */
        .password-form {
          width: 100%;
          text-align: left;
          margin-bottom: 4px;
        }

        .password-field {
          margin-bottom: 16px;
        }

        .password-label {
          display: block;
          font-size: 13px;
          font-weight: 600;
          color: #333;
          margin-bottom: 6px;
        }

        .password-input-wrapper {
          position: relative;
          display: flex;
          align-items: center;
        }

        .password-input {
          width: 100%;
          padding: 10px 40px 10px 14px;
          border: 2px solid #e5e7eb;
          border-radius: 10px;
          font-size: 14px;
          transition: all 0.3s ease;
          background: #fafafa;
          color: #1a1a1a;
        }

        .password-input:focus {
          outline: none;
          border-color: #1E88E5;
          background: #ffffff;
          box-shadow: 0 0 0 4px rgba(30, 136, 229, 0.08);
        }

        .password-input.error {
          border-color: #C62828;
        }

        .password-input.error:focus {
          box-shadow: 0 0 0 4px rgba(198, 40, 40, 0.08);
        }

        .password-input:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .password-toggle {
          position: absolute;
          right: 10px;
          background: none;
          border: none;
          color: #94a3b8;
          cursor: pointer;
          padding: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: color 0.3s ease;
        }

        .password-toggle:hover {
          color: #1a1a1a;
        }

        .password-error {
          display: block;
          font-size: 12px;
          color: #C62828;
          margin-top: 4px;
        }

        .password-error-general {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 14px;
          background: #fef3f2;
          border: 1px solid rgba(198, 40, 40, 0.15);
          border-radius: 10px;
          color: #C62828;
          font-size: 13px;
          width: 100%;
          margin-bottom: 16px;
        }

        .password-success {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 14px;
          background: #e8f5e9;
          border: 1px solid rgba(46, 125, 50, 0.15);
          border-radius: 10px;
          color: #2E7D32;
          font-size: 13px;
          width: 100%;
          margin-bottom: 16px;
          animation: successPulse 1.5s ease;
        }

        @keyframes successPulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.02); }
        }

        /* Password Strength */
        .password-strength {
          margin-top: 8px;
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .strength-bar-container {
          flex: 1;
          height: 4px;
          background: #e5e7eb;
          border-radius: 4px;
          overflow: hidden;
        }

        .strength-bar {
          height: 100%;
          border-radius: 4px;
          transition: width 0.3s ease, background-color 0.3s ease;
        }

        .strength-label {
          font-size: 11px;
          font-weight: 600;
          min-width: 60px;
          text-align: right;
        }

        /* Password Requirements */
        .password-requirements {
          margin-top: 12px;
          padding: 12px 14px;
          background: #f8f9fa;
          border-radius: 10px;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 6px;
        }

        .requirement {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          color: #94a3b8;
          transition: color 0.3s ease;
        }

        .requirement.met {
          color: #2E7D32;
        }

        .requirement-icon {
          display: inline-block;
          width: 16px;
          height: 16px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 11px;
          font-weight: 700;
          color: #94a3b8;
          border: 2px solid #e5e7eb;
          transition: all 0.3s ease;
        }

        .requirement.met .requirement-icon {
          background: #2E7D32;
          color: white;
          border-color: #2E7D32;
        }

        /* Loading Spinner */
        .loading-spinner {
          display: inline-block;
          width: 18px;
          height: 18px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top: 2px solid #ffffff;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        /* Responsive */
        @media (max-width: 1024px) {
          .ict-header {
            padding: 10px 20px;
          }
          .search-box {
            min-width: 200px;
          }
          .quick-status-icons {
            display: none;
          }
        }

        @media (max-width: 768px) {
          .ict-header {
            padding: 8px 16px;
            flex-wrap: wrap;
            gap: 8px;
            min-height: 60px;
          }

          .search-box {
            min-width: 100%;
            order: 3;
          }

          .header-center {
            flex: 1;
            gap: 8px;
          }

          .system-status-badge {
            display: none;
          }

          .digital-clock {
            padding: 4px 12px;
          }

          .clock-time {
            font-size: 12px;
          }

          .clock-date {
            display: none;
          }

          .profile-info {
            display: none;
          }

          .profile {
            padding: 4px;
          }

          .profile-dropdown {
            right: -20px;
            width: 280px;
          }

          .dialog-container {
            padding: 32px 24px 24px;
            max-width: 380px;
          }

          .password-dialog {
            max-width: 380px;
          }

          .password-requirements {
            grid-template-columns: 1fr;
          }

          .dialog-actions {
            flex-direction: column;
          }

          .status-grid {
            grid-template-columns: 1fr 1fr;
          }
        }

        @media (max-width: 480px) {
          .dialog-container {
            padding: 24px 16px 16px;
            max-width: 340px;
          }

          .password-dialog {
            max-width: 340px;
          }

          .digital-clock .clock-divider {
            display: none;
          }

          .profile-dropdown {
            width: 260px;
            right: -10px;
          }
        }
      `}</style>
    </>
  );
};

export default Header;